/**
 * errorDetector.ts — Detección de errores graves en ODTs procesados
 *
 * Analiza el array de ProcessedInfrastructure resultante del pipeline y
 * detecta patrones de error estructural que impiden o degradan la geocodificación.
 *
 * Se usa como librería por GUIA-DEVOLUCION-01 (Worker email) y, en sesión
 * separada, por GUIA-DEVOLUCION-02 (integración Step 2 de React).
 *
 * @module services/analysis/errorDetector
 */

import type { ProcessedInfrastructure } from '../../types/processing';

// ═════════════════════════════════════════════════════════════════════════════
// TIPOS PÚBLICOS
// ═════════════════════════════════════════════════════════════════════════════

export type SevereErrorCode =
  | 'ERR-1'  // Separador decimal europeo (coma en lugar de punto)
  | 'ERR-2'  // Lat/lon en lugar de UTM (valores < 180)
  | 'ERR-3'  // Coordenadas truncadas (< 5 dígitos)
  | 'ERR-4'  // Alto porcentaje de celdas Y vacías (> 30 %)
  | 'ERR-5'  // Denominación genérica sin topónimo
  | 'ERR-6'; // Nombre de infraestructura genérico

export interface SevereError {
  /** Código identificador del error */
  code: SevereErrorCode;
  /** Número de infraestructuras afectadas */
  count: number;
  /** Total de infraestructuras procesadas */
  total: number;
  /** Porcentaje afectado (0-100, redondeado a 1 decimal) */
  percentage: number;
  /** Referencia legible a la sección de la guía de calidad */
  guiaRef: string;
  /** Fragmento de URL para enlace directo a la sección de la guía */
  guiaAnchor: string;
  /** Instrucción específica para el técnico municipal */
  instruccionEspecifica: string;
}

export interface ErrorSummary {
  /** Error de mayor impacto (mayor porcentaje) */
  dominantError: SevereError;
  /** Todos los errores detectados, ordenados de mayor a menor porcentaje */
  allErrors: SevereError[];
  /**
   * Severidad global:
   * - 'critical': el error dominante supera el 30 % de infras
   * - 'warning': entre el umbral mínimo y el 30 %
   */
  overallSeverity: 'warning' | 'critical';
}

// ═════════════════════════════════════════════════════════════════════════════
// CONSTANTES: UMBRALES Y DESCRIPCIONES
// ═════════════════════════════════════════════════════════════════════════════

/** Umbral mínimo para reportar un error: ≥ 5 % de infras O ≥ 3 en absoluto */
export const ERROR_THRESHOLDS = {
  MIN_PERCENTAGE: 5,
  MIN_ABSOLUTE: 3,
  CRITICAL_PERCENTAGE: 30,
} as const;

/** Descripción de cada código de error (guiaRef, anchor, instrucción) */
export const ERROR_DESCRIPTIONS: Record<
  SevereErrorCode,
  Pick<SevereError, 'guiaRef' | 'guiaAnchor' | 'instruccionEspecifica'>
> = {
  'ERR-1': {
    guiaRef: 'Error 1 — Separador decimal europeo (coma)',
    guiaAnchor: '#error-1',
    instruccionEspecifica:
      'Las coordenadas X o Y usan coma como separador decimal (p. ej., «456789,12»). ' +
      'Sustituya la coma por punto decimal en todas las celdas de coordenadas del documento ODT.',
  },
  'ERR-2': {
    guiaRef: 'Error 2 — Coordenadas en latitud/longitud en lugar de UTM',
    guiaAnchor: '#error-2',
    instruccionEspecifica:
      'Los valores de X e Y son menores de 180, lo que indica coordenadas geográficas ' +
      '(latitud/longitud WGS84). El sistema requiere coordenadas UTM huso 30 (ETRS89, EPSG:25830). ' +
      'Los valores correctos son del orden X ≈ 100 000–900 000, Y ≈ 3 900 000–4 400 000.',
  },
  'ERR-3': {
    guiaRef: 'Error 3 — Coordenadas truncadas',
    guiaAnchor: '#error-3',
    instruccionEspecifica:
      'Las coordenadas tienen menos de 5 dígitos, lo que indica truncamiento o pérdida de ' +
      'información. Revise el origen de los datos y asegúrese de copiar el valor completo ' +
      'de la coordenada UTM sin eliminar dígitos.',
  },
  'ERR-4': {
    guiaRef: 'Error 4 — Alto porcentaje de coordenadas Y vacías',
    guiaAnchor: '#error-4',
    instruccionEspecifica:
      'Más del 30 % de las infraestructuras no tienen valor en la columna Y (coordenada Norte). ' +
      'Revise que la columna Y esté correctamente identificada y que ninguna celda se haya ' +
      'dejado vacía involuntariamente.',
  },
  'ERR-5': {
    guiaRef: 'Error 5 — Denominación genérica sin topónimo',
    guiaAnchor: '#error-5',
    instruccionEspecifica:
      'Varias infraestructuras tienen denominaciones genéricas (p. ej., «Colegio», «Centro de Salud») ' +
      'sin incluir el nombre o topónimo específico. Incluya el nombre oficial completo para ' +
      'permitir la geocodificación automática.',
  },
  'ERR-6': {
    guiaRef: 'Error 6 — Nombre de infraestructura genérico o incompleto',
    guiaAnchor: '#error-6',
    instruccionEspecifica:
      'Varios nombres de infraestructura son demasiado genéricos o están incompletos. ' +
      'Asegúrese de incluir el nombre oficial completo tal como aparece en el directorio ' +
      'de referencia correspondiente (SAS, Consejería de Educación, etc.).',
  },
};

// ═════════════════════════════════════════════════════════════════════════════
// HELPERS INTERNOS DE DETECCIÓN
// ═════════════════════════════════════════════════════════════════════════════

/** Devuelve true si el string parece contener una coma como separador decimal UTM */
function hasEuropeanDecimalSeparator(val: string): boolean {
  if (!val) return false;
  // Coma en posición de decimal: dígitos, coma, 1-3 dígitos al final
  return /\d,\d{1,3}$/.test(val.trim());
}

/** Devuelve true si el valor numérico indica lat/lon (< 180) en lugar de UTM */
function isLatLon(val: string): boolean {
  if (!val) return false;
  const n = parseFloat(val.replace(',', '.'));
  return !isNaN(n) && Math.abs(n) < 180;
}

/** Devuelve true si el valor numérico tiene menos de 5 dígitos (truncado) */
function isTruncated(val: string): boolean {
  if (!val) return false;
  const digits = val.replace(/[^0-9]/g, '');
  return digits.length > 0 && digits.length < 5;
}

/** Devuelve true si el nombre parece genérico (sin topónimo diferenciador) */
function isGenericName(name: string): boolean {
  if (!name) return true;
  const normalized = name.trim().toLowerCase();
  const GENERIC_PATTERNS = [
    /^(colegio|instituto|escuela|centro de salud|hospital|ambulatorio|consultorio)$/,
    /^(polideportivo|pabellón|campo de fútbol|piscina)$/,
    /^(ayuntamiento|casa consistorial|juzgado)$/,
    /^(iglesia|ermita|parroquia)$/,
    /^(cuartel|comisaría|policía local)$/,
    /^(parque de bomberos|bomberos)$/,
    /^(s\/n|sin nombre|nd|n\/a|\?+|[-]+)$/,
  ];
  return GENERIC_PATTERNS.some(p => p.test(normalized));
}

/** Construye un SevereError dado el código y las infras afectadas */
function buildSevereError(
  code: SevereErrorCode,
  count: number,
  total: number,
): SevereError {
  const desc = ERROR_DESCRIPTIONS[code];
  return {
    code,
    count,
    total,
    percentage: total > 0 ? Math.round((count / total) * 1000) / 10 : 0,
    ...desc,
  };
}

/** Devuelve true si un error supera el umbral mínimo de reporte */
function exceedsThreshold(count: number, total: number): boolean {
  if (total === 0) return false;
  const pct = (count / total) * 100;
  return pct >= ERROR_THRESHOLDS.MIN_PERCENTAGE || count >= ERROR_THRESHOLDS.MIN_ABSOLUTE;
}

// ═════════════════════════════════════════════════════════════════════════════
// FUNCIÓN PÚBLICA PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Detecta errores graves en un array de infraestructuras procesadas.
 *
 * Analiza los valores originales (xOriginal, yOriginal, nombre) para identificar
 * patrones de error estructural que impiden o degradan la geocodificación.
 *
 * @param results - Array de ProcessedInfrastructure del pipeline PTEL
 * @param opts.skip - Predicado opcional: si devuelve true para una fila, esa
 *        fila NO suma a ningún contador de error, PERO sigue contando en el
 *        denominador (`total`). Uso en P2: `skip: isResolved` para no marcar
 *        como error las filas que la geocodificación ya rescató (evita el falso
 *        positivo de ERR-4 sobre `yOriginal` vacía en documentos resueltos por
 *        dirección — OBS-P2-REJECTION-BANNER-PCT-SOSPECHOSO). Sin `skip` el
 *        comportamiento es idéntico al histórico (contrato puro sobre origen).
 * @returns ErrorSummary con el error dominante y todos los errores detectados,
 *          o null si no se detecta ningún error que supere el umbral mínimo.
 */
export function detectSevereErrors(
  results: ProcessedInfrastructure[],
  opts?: { skip?: (inf: ProcessedInfrastructure) => boolean },
): ErrorSummary | null {
  const total = results.length;
  if (total === 0) return null;
  const skip = opts?.skip;

  // Contadores por tipo de error
  let err1 = 0; // Separador europeo
  let err2 = 0; // Lat/lon
  let err3 = 0; // Truncado
  let err4 = 0; // Y vacía
  let err5 = 0; // Denominación genérica (nombre)
  let err6 = 0; // Nombre incompleto / genérico (alias err5 para nombre)

  for (const inf of results) {
    // Fila ya resuelta por el pipeline (p. ej. geocodificada por dirección):
    // no la contamos como error, pero sigue en el denominador `total`.
    if (skip?.(inf)) continue;
    const xRaw = inf.xOriginal ?? '';
    const yRaw = inf.yOriginal ?? '';
    const nombre = inf.nombre ?? '';

    // ERR-4: Y vacía (comprobamos primero, independiente de formato)
    if (!yRaw || yRaw.trim() === '') {
      err4++;
      continue; // Si Y está vacía, no tiene sentido evaluar formato de Y
    }

    // ERR-1: Separador europeo en X o Y
    if (hasEuropeanDecimalSeparator(xRaw) || hasEuropeanDecimalSeparator(yRaw)) {
      err1++;
    }

    // ERR-2: Lat/lon — solo si no era ya coma (evita doble conteo)
    else if (
      (xRaw && isLatLon(xRaw)) ||
      (yRaw && isLatLon(yRaw))
    ) {
      err2++;
    }

    // ERR-3: Truncado
    else if (
      (xRaw && isTruncated(xRaw)) ||
      (yRaw && isTruncated(yRaw))
    ) {
      err3++;
    }

    // ERR-5 y ERR-6 son independientes del formato de coordenadas
    if (isGenericName(nombre)) {
      err5++;
    }
  }

  // ERR-6: nombres de 1-2 palabras que no son claramente un topónimo
  // (diferente de ERR-5: aquí detectamos brevedad excesiva, no patrón exacto)
  for (const inf of results) {
    if (skip?.(inf)) continue;
    const nombre = (inf.nombre ?? '').trim();
    const words = nombre.split(/\s+/).filter(Boolean);
    if (words.length > 0 && words.length <= 2 && !isGenericName(nombre)) {
      err6++;
    }
  }

  // Construir candidatos a error grave
  const candidates: Array<[SevereErrorCode, number]> = [
    ['ERR-1', err1],
    ['ERR-2', err2],
    ['ERR-3', err3],
    ['ERR-4', err4],
    ['ERR-5', err5],
    ['ERR-6', err6],
  ];

  const detected: SevereError[] = candidates
    .filter(([, count]) => exceedsThreshold(count, total))
    .map(([code, count]) => buildSevereError(code, count, total))
    .sort((a, b) => b.percentage - a.percentage);

  if (detected.length === 0) return null;

  const dominantError = detected[0];
  const overallSeverity: 'warning' | 'critical' =
    dominantError.percentage >= ERROR_THRESHOLDS.CRITICAL_PERCENTAGE
      ? 'critical'
      : 'warning';

  return { dominantError, allErrors: detected, overallSeverity };
}
